import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Loader2,
  ShieldCheck,
  FileText,
  HelpCircle,
  Mail,
  Megaphone,
} from "lucide-react";
import { getPolicy } from "../../../redux/policy/policyAction";
import styles from "./PolicyView.module.scss";

const POLICY_METADATA = {
  PrivacyPolicy: {
    title: "Privacy Policy",
    icon: <ShieldCheck size={48} />,
    description:
      "Learn how we collect, use, and protect your personal information.",
  },
  TermsAndConditions: {
    title: "Terms & Conditions",
    icon: <FileText size={48} />,
    description:
      "Read our rules and regulations for using the BizAura platform.",
  },
  HelpAndFAQ: {
    title: "Help & FAQ",
    icon: <HelpCircle size={48} />,
    description: "Find answers to frequently asked questions and get support.",
  },
  ContactUs: {
    title: "Contact Us",
    icon: <Mail size={48} />,
    description: "Get in touch with our team for any inquiries or support.",
  },
  AdvertiseWithUs: {
    title: "Advertise With Us",
    icon: <Megaphone size={48} />,
    description: "Partner with us to reach more businesses and grow together.",
  },
};

const PolicyView = () => {
  const { type } = useParams();
  const dispatch = useDispatch();
  const { policies, isLoading, error } = useSelector((state) => state.policy);

  const policyData = policies[type];
  const meta = POLICY_METADATA[type] || {
    title: "Policy",
    icon: <FileText size={48} />,
  };

  useEffect(() => {
    console.log(`[PolicyView] Mounted for type: ${type}`);
    document.title = `${meta.title} | BizAura`;

    if (type && !policies[type]) {
      dispatch(getPolicy(type));
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [dispatch, type, policies, meta.title]);

  if (isLoading && !policyData) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
        <p>Loading {meta.title}...</p>
      </div>
    );
  }

  if (error && !policyData) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>!</div>
        <h3>Oops! Something went wrong</h3>
        <p>{error}</p>
        <button
          onClick={() => dispatch(getPolicy(type))}
          className={styles.retryBtn}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.policyPage}>
      {/* Hero Header */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroContent}>
            <div className={styles.policyIcon}>{meta.icon}</div>
            <h1 className={styles.title}>{policyData?.titel || meta.title}</h1>
            <p className={styles.subtitle}>{meta.description}</p>
            {policyData?.updateDate && (
              <p className={styles.lastUpdated}>
                Last updated:{" "}
                {new Date(policyData.updateDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          <div className={styles.contentCard}>
            {policyData?.description ? (
              <div
                className={styles.richText}
                dangerouslySetInnerHTML={{ __html: policyData.description }}
              />
            ) : (
              <div className={styles.noContent}>
                <p>
                  Policy content is currently being updated. Please check back
                  soon.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PolicyView;
